import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { directDealFiltersConstant } from '../constants/filter/directDealFilter.constant';
import { marketPriceFiltersConstant } from '../constants/filter/marketPriceFilter.constant';
import { productFiltersConstant } from '../constants/filter/productFilter.constant';
import { userOwnTractorFiltersConstant } from '../constants/filter/userOwnTractorFilter.constant';
import { FilterOptionValueType, FilterType, IFilter, IFilterOption } from '../interfaces/Filter.interfaces';
import { changeFilterOptionIsSelected } from '../utils/filter/changeFilterOptionIsSelected.util';
import { changeFilterOptionValue } from '../utils/filter/changeFilterOptionValue.util';
import { resetFilterOptionIsSelected } from '../utils/filter/resetFilterOptionIsSelected.util';

type filterStateType = {
  isFilterOptionsAdded: {
    brands: boolean;
  };
  isSearchParamChecked: boolean;
  clickedFilterButtonIndex?: number;
  haveToRefreshLocalStates: boolean;
  globalFilters: IFilter[];
  selectedFilterKey: string | null;
  isAutoOpened: boolean;
};

type initialFilterStateType = {
  productFilter: filterStateType;
  userOwnTractorFilter: filterStateType;
  marketPriceFilter: filterStateType;
  directDealFilter: filterStateType;
};

const initialState: initialFilterStateType = {
  productFilter: {
    isFilterOptionsAdded: {
      brands: false,
    },
    isSearchParamChecked: false,
    haveToRefreshLocalStates: false,
    globalFilters: productFiltersConstant,
    selectedFilterKey: null,
    isAutoOpened: false,
  },
  userOwnTractorFilter: {
    isFilterOptionsAdded: {
      brands: false,
    },
    isSearchParamChecked: false,
    haveToRefreshLocalStates: false,
    globalFilters: userOwnTractorFiltersConstant,
    selectedFilterKey: null,
    isAutoOpened: false,
  },
  marketPriceFilter: {
    isFilterOptionsAdded: {
      brands: false,
    },
    isSearchParamChecked: false,
    haveToRefreshLocalStates: false,
    globalFilters: marketPriceFiltersConstant,
    selectedFilterKey: null,
    isAutoOpened: false,
  },
  directDealFilter: {
    isFilterOptionsAdded: {
      brands: false,
    },
    isSearchParamChecked: false,
    haveToRefreshLocalStates: false,
    globalFilters: directDealFiltersConstant,
    selectedFilterKey: null,
    isAutoOpened: false,
  },
};

export const filterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    // 필터 옵션의 추가 여부를 관리하는 Action
    changeIsFilterOptionsAdded: (
      state,
      action: PayloadAction<{ filterType: FilterType; data: 'brands'; value: boolean }>,
    ) => {
      if (action.payload.data === 'brands') {
        state[action.payload.filterType].isFilterOptionsAdded.brands = action.payload.value;
      }
    },

    changeIsSearchParamChecked: (state, action: PayloadAction<{ filterType: FilterType; value: boolean }>) => {
      state[action.payload.filterType].isSearchParamChecked = action.payload.value;
    },

    changeClickedFilterButtonIndex: (
      state,
      action: PayloadAction<{ filterType: FilterType; value: number | undefined }>,
    ) => {
      state[action.payload.filterType].clickedFilterButtonIndex = action.payload.value;
    },

    changeHaveToRefreshLocalStates: (state, action: PayloadAction<{ filterType: FilterType; value: boolean }>) => {
      state[action.payload.filterType].haveToRefreshLocalStates = action.payload.value;
    },

    resetAllFilters: (state, action: PayloadAction<{ filterType: FilterType }>) => {
      const newFilters = resetFilterOptionIsSelected({
        currentFilters: state[action.payload.filterType].globalFilters,
      });
      state[action.payload.filterType].globalFilters = newFilters;
    },

    resetOneFilter: (
      state,
      action: PayloadAction<{
        filterType: FilterType;
        filter: IFilter;
      }>,
    ) => {
      const newFilters = resetFilterOptionIsSelected({
        currentFilters: state[action.payload.filterType].globalFilters,
        targetFilter: action.payload.filter,
      });
      state[action.payload.filterType].globalFilters = newFilters;
    },

    changeFilters: (
      state,
      action: PayloadAction<{
        filterType: FilterType;
        filters: IFilter[];
      }>,
    ) => {
      state[action.payload.filterType].globalFilters = action.payload.filters;
    },

    addFilterOptions: (
      state,
      action: PayloadAction<{
        filterType: FilterType;
        filterKey: string;
        options: IFilterOption[];
      }>,
    ) => {
      const newFilters = state[action.payload.filterType].globalFilters.map((filter) => {
        if (filter.key === action.payload.filterKey) {
          filter.options = action.payload.options;
        }
        return filter;
      });

      state[action.payload.filterType].globalFilters = newFilters;
    },

    changeFilterOptionIsSelected: (
      state,
      action: PayloadAction<{
        filterType: FilterType;
        targetFilter: IFilter;
        targetOption: IFilterOption;
      }>,
    ) => {
      const newFilters = changeFilterOptionIsSelected({
        currentFilters: state[action.payload.filterType].globalFilters,
        targetFilter: action.payload.targetFilter,
        targetOption: action.payload.targetOption,
      });

      state[action.payload.filterType].globalFilters = newFilters;
    },

    changeFilterOptionValue: (
      state,
      action: PayloadAction<{
        filterType: FilterType;
        targetFilter: IFilter;
        optionValues: FilterOptionValueType[];
      }>,
    ) => {
      const newFilters = changeFilterOptionValue({
        currentFilters: state[action.payload.filterType].globalFilters,
        targetFilter: action.payload.targetFilter,
        newOptionValues: action.payload.optionValues,
      });

      state[action.payload.filterType].globalFilters = newFilters;
    },

    setSelectedFilterKey: (state, action: PayloadAction<{ filterType: FilterType; value: string | null }>) => {
      state[action.payload.filterType].selectedFilterKey = action.payload.value;
    },

    setIsAutoOpened: (state, action: PayloadAction<{ filterType: FilterType; value: boolean }>) => {
      state[action.payload.filterType].isAutoOpened = action.payload.value;
    },
  },
});
